import { Suspense, lazy, useContext, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useBoolean } from "usehooks-ts";

import { AssessmentGradingFormContext } from "../../../providers/AssessmentGradingFormProvider";

import { switchOrg } from "api/api-server-actions";
import { serverGetPageAssessmentStatuses } from "api/api-server-data";
import { serverFetchLatestPublications } from "api/api-server-publishing";
import { getSectionUserData } from "api/api-worksheets";
import { LoadingScreen } from "components/LoadingScreen";
import { MobileRoutingOverlay } from "components/MobileRoutingOverlay";
import { useAITool } from "components/ai/hooks/use-ai-tool";
import { withActiveAv } from "components/av/AvSubsystemLoader";
import { MainFooter } from "components/footer/MainFooter";
import { MainHeader } from "components/header/MainHeader";
import { MobileMainHeader } from "components/header/MobileMainHeader";
import { isProjectorView } from "components/materials/presentation/projector/projector-messaging";
import { t } from "i18n/i18n";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { BypassReleaseWarningContext } from "providers/BypassReleaseWarningProvider";
import { KeyboardShortcutsController } from "providers/KeyboardShortcutsController";
import { PubsubWebsocketContext } from "providers/PubsubWebsocketProvider";
import { ShareDBProvider } from "providers/ShareDBProvider";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentClassUser,
  selectCurrentSectionId,
  selectCurrentSectionIsTemplate,
  selectCurrentSectionRole,
  selectCurrentUserId,
  selectIsClassSessionVirtual,
  selectIsRecordingAgent,
  selectIsSidebarOpen,
  selectOrgIdForSectionId,
} from "store/selectors";
import {
  selectClassStateLastUpdatedAt,
  selectIsObservingVirtualClass,
  selectStableIsMainAvTab,
} from "store/selectors/pubsub-virtual-class-state";
import { attendanceStateActions } from "store/slices/attendance";
import { computedStateActions } from "store/slices/computed";
import { PubsubVirtualClassStateActions } from "store/slices/pubsub-virtual-class-state";
import { trackUserProperty } from "utils/amplitude";
import { getOrgIdFromCookie, setLastSectionIdCookie } from "utils/auth";
import { useAv, useAvPubsubMessaging } from "utils/av";
import { isTestEnv } from "utils/env";
import { logInfoWithDebugFlag, rollbarAndLogError, rollbarAndLogWarning } from "utils/logger";
import { isTemplateSection } from "utils/publishing";
import { Outlet, useParams } from "utils/routing";
import { isAVEnabled } from "utils/waffle-flags";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

const LazyParticipantBar = withActiveAv(
  () => import("components/av/ParticipantBar"),
  t("av.loading_users"),
  true,
);

const AvContextProvider = lazy(() => import("components/av/AvProvider"));

export default function Layout() {
  const [isAiAssessmentLoading, setIsAiAssessmentLoading] = useState(false);
  const isMobile = !useIsMdOrLarger();
  const isSidebarOpen = useAppSelector(selectIsSidebarOpen);

  // Ensure this component refreshes when we navigate between section routes
  const { sectionId } = useParams();
  // It's important we lazy-load any entry to the AV subsystem, as it uses many heavy dependencies
  // and is only rarely enabled.
  const orgIdForSectionId = useAppSelector((s) => selectOrgIdForSectionId(s, sectionId));
  const orgIdFromCookie = getOrgIdFromCookie();
  const userId = useAppSelector(selectCurrentUserId);
  const { canAuthorSection } = useCapabilities();
  const dispatch = useAppDispatch();
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const isTemplate = useAppSelector(selectCurrentSectionIsTemplate);

  const classUser = useAppSelector(selectCurrentClassUser);
  const isProjectorPath = isProjectorView();

  const { pubsubStatus } = useContext(PubsubWebsocketContext);

  const { sendPubsubVirtualClassStateMessage } = useAvPubsubMessaging();

  const stableIsMainAvTab = useAppSelector(selectStableIsMainAvTab);
  const hasVirtualClassPubsubUpdate = useAppSelector(selectClassStateLastUpdatedAt) !== null;

  useAITool("read_page");

  const sectionRole = useAppSelector(selectCurrentSectionRole);
  useEffect(() => {
    if (sectionRole) {
      trackUserProperty("courseRole", sectionRole);
    }
  }, [sectionRole]);

  const isClassSessionVirtual = useAppSelector(selectIsClassSessionVirtual);

  const isRecordingAgent = useAppSelector(selectIsRecordingAgent);
  useEffect(() => {
    if (classUser?.id && isRecordingAgent) {
      storeApi.class_users.partial_update({ id: classUser?.id, is_in_class_session: true });
    }
  }, [isRecordingAgent, classUser?.id]);

  // Only used to determine if we should be in dark mode or not.
  const { isInVirtualClass: isDarkMode } = useAv();

  // We consider for this component that a user is in a virtual class if the virtual class exists
  // and the user is either participating or performing an AV check to get in.
  const isUserInVirtualClass =
    !isProjectorPath &&
    isClassSessionVirtual &&
    (classUser?.is_in_class_session || classUser?.is_performing_av_check);

  // Virtual class setup: instructor is performing AV check
  const isVirtualClassSetup = useMemo(
    () => canAuthorSection && classUser?.is_performing_av_check,
    [canAuthorSection, classUser?.is_performing_av_check],
  );

  // We should consider starting the AV subsystem if the user is in a virtual class session or setup.
  const isVirtualSessionOrSetup = isAVEnabled() && (isUserInVirtualClass || isVirtualClassSetup);

  // We should request the AV state if the pubsub websocket is connected to make sure the join message is sent.
  const shouldRequestAvState = pubsubStatus === "connected" && isVirtualSessionOrSetup;

  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);

  const shouldStartAv = stableIsMainAvTab || isObservingClass;

  // This useEffect ensures we log the state of shouldStartAv whenever it changes,
  // which is useful for debugging the AV subsystem mounting and unmounting.
  useEffect(() => {
    if (!isTestEnv) {
      logInfoWithDebugFlag(
        "debug_virtual_class",
        "[Layout] shouldStartAv changed:",
        shouldStartAv,
        {
          shouldRequestAvState,
          hasVirtualClassPubsubUpdate,
          stableIsMainAvTab,
          pubsubStatus,
          isProjectorPath,
          avEnabled: isAVEnabled(),
          isUserInVirtualClass,
          isVirtualClassSetup,
          classUser: {
            is_in_class_session: classUser?.is_in_class_session,
            is_performing_av_check: classUser?.is_performing_av_check,
          },
        },
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldStartAv]);

  useEffect(() => {
    if (isObservingClass) return;
    if (shouldRequestAvState && !hasVirtualClassPubsubUpdate) {
      logInfoWithDebugFlag(
        "debug_virtual_class",
        "[Layout] Virtual Class. Sending join message, force: false",
      );
      sendPubsubVirtualClassStateMessage({
        action: "join",
        force: isRecordingAgent || false,
      });
    } else if (hasVirtualClassPubsubUpdate && !shouldRequestAvState) {
      if (stableIsMainAvTab) {
        logInfoWithDebugFlag(
          "debug_virtual_class",
          "[Layout] Virtual Class. Sending leave message",
        );
        sendPubsubVirtualClassStateMessage({ action: "leave" });
      }
      logInfoWithDebugFlag(
        "debug_virtual_class",
        "[Layout] Clearing virtual class state not in AV tab nor observing",
      );
      dispatch(PubsubVirtualClassStateActions.clearVirtualClassState());
    }
  }, [
    shouldRequestAvState,
    isObservingClass,
    hasVirtualClassPubsubUpdate,
    stableIsMainAvTab,
    sendPubsubVirtualClassStateMessage,
    dispatch,
    sectionId,
    isRecordingAgent,
  ]);

  const needsOrgSwitch = orgIdForSectionId && orgIdForSectionId !== orgIdFromCookie;

  useEffect(() => {
    if (!userId || !sectionId) {
      return;
    }
    // If we detect that we need to switch org, we skip the course retrieval
    if (needsOrgSwitch) {
      return;
    }
    storeApi.sections.retrieve(sectionId, { skipToast: true }).catch((error) => {
      if ([404, 403, 401].includes(error.status)) {
        rollbarAndLogWarning("Course not found, rerouting to index", error);
        window.location.href = "/";
      } else {
        rollbarAndLogError("Failed to load course", error);
      }
    });
  }, [sectionId, userId, needsOrgSwitch]);

  // If we try to load a course URL that doesn't exist, but it _does_ exist in another org
  // that the user has access to, we switch org and reload the page.
  useEffect(() => {
    if (needsOrgSwitch) {
      switchOrg(orgIdForSectionId)
        .then(() => {
          window.location.reload();
        })
        .catch((error) => {
          // We don't show the error the user, because it's too technical. They will just see a 404.
          rollbarAndLogError("Failed to auto-switch org", error);
        });
    }
  }, [needsOrgSwitch, orgIdForSectionId]);

  // Whenever we navigate to a course, we want to remember which course it was, so that accessing
  // the app root will preferably load the same course again.
  useEffect(() => {
    if (sectionId) setLastSectionIdCookie(sectionId);
  }, [sectionId]);

  useEffect(() => {
    const loadSectionData = async () => {
      if (!userId || !sectionId) {
        return;
      }

      storeApi.courses.list();
      storeApi.terms.list();
      storeApi.class_sessions.list({ section_id: sectionId });
      storeApi.topics.list({ section_id: sectionId });
      storeApi.activities.list({ section_id: sectionId });
      storeApi.pages.list({ section_id: sectionId });
      storeApi.page_access_data.list({ section_id: sectionId });
      storeApi.recording_sessions.list({ section_id: sectionId });
      storeApi.outcomes.list();
      storeApi.section_outcomes.list({ section_id: sectionId });
      storeApi.page_outcomes.list({ section_id: sectionId });
      const sections = await storeApi.sections.list();
      const sectionUsers = await storeApi.section_users.list({ section_id: sectionId });
      const statuses = await serverGetPageAssessmentStatuses({ sectionId });
      // Dispatch statuses immediately as each batch completes
      dispatch(computedStateActions.savePageAssessmentStatuses(statuses));

      // Get or create class user if not exists
      const classUsers = await storeApi.class_users.list({ section_id: sectionId });

      const currentSectionUserId = sectionUsers.find(
        (sectionUser) => sectionUser.user_id === userId,
      )?.id;

      if (canAuthorSection) {
        const currentSection = sections.find((s) => s.id === sectionId);
        if (isTemplateSection(currentSection)) {
          serverFetchLatestPublications(sectionId);
        }
      }

      // Even though ClassUsers reference user_id (not section_user_id), we need to ensure the section user exists
      // before creating a class user. Upon create, the server calls get_section_user() to sync breakout groups;
      // without a section user that lookup would fail.
      if (!currentSectionUserId) {
        logInfoWithDebugFlag(
          "debug_virtual_class",
          "Current sectionUser not found: not creating class user",
          { sectionId, userId },
        );

        return;
      }

      const currentClassUser = classUsers.find((cu) => cu.user_id === userId);
      if (!currentClassUser && !isTemplate) {
        logInfoWithDebugFlag("debug_virtual_class", "Creating class user", { sectionId, userId });
        await storeApi.class_users.create({
          section_id: sectionId,
          user_id: userId,
        });
      }
    };

    // Once we have the course and know it exists, we can start loading all related data
    if (sectionId && currentSectionId === sectionId && !!userId) {
      loadSectionData();
    }
    // eslint-disable-next-line no-object-deps/no-object-deps
  }, [currentSectionId, sectionId, dispatch, userId, isTemplate, canAuthorSection]);

  useEffect(() => {
    const getSectionActivityData = async () => {
      if (!sectionId) return;
      const activityData = await getSectionUserData(sectionId);
      dispatch(attendanceStateActions.updateAnalytics(activityData));
    };

    if (userId && sectionId) {
      getSectionActivityData();
    }
  }, [sectionId, dispatch, userId]);

  const { value: showWarning, setTrue: resetWarning, setFalse: bypassWarning } = useBoolean(true);
  const bypassReleaseWarningValue = useMemo(
    () => ({ showWarning, resetWarning, bypassWarning }),
    [showWarning, resetWarning, bypassWarning],
  );

  const layoutContent = (
    <div className={twMerge("flex h-full flex-col", isDarkMode && "bg-black-tint-15")}>
      {!isObservingClass && <KeyboardShortcutsController />}
      {!isProjectorPath && (isMobile ? <MobileMainHeader /> : <MainHeader />)}
      {shouldStartAv && <LazyParticipantBar />}
      <div className="flex h-1 grow" data-sidebar-open={isMobile ? undefined : isSidebarOpen}>
        <div className={twMerge("relative h-full max-w-full grow", isDarkMode && "px-3")}>
          <ErrorBoundary>
            <MobileRoutingOverlay />
            <Outlet />
          </ErrorBoundary>
        </div>
      </div>
      {!isProjectorPath && <MainFooter />}
    </div>
  );

  const gradingFormContextValue = useMemo(
    () => ({ isAiAssessmentLoading, setIsAiAssessmentLoading }),
    [isAiAssessmentLoading, setIsAiAssessmentLoading],
  );

  return (
    <ShareDBProvider>
      <BypassReleaseWarningContext.Provider value={bypassReleaseWarningValue}>
        <AssessmentGradingFormContext.Provider value={gradingFormContextValue}>
          {shouldStartAv ? (
            <Suspense fallback={<LoadingScreen>{t("av.loading_av_subsystem")}</LoadingScreen>}>
              <AvContextProvider>{layoutContent}</AvContextProvider>
            </Suspense>
          ) : (
            layoutContent
          )}
        </AssessmentGradingFormContext.Provider>
      </BypassReleaseWarningContext.Provider>
    </ShareDBProvider>
  );
}
